/* cspell:ignore noninteractive */
/* eslint-disable jsx-a11y/no-noninteractive-tabindex --
   The scrollable <pre> is intentionally focusable for keyboard arrow-scroll
   (axe rule: scrollable-region-focusable). */
import cx from "classnames";
import { ReactNode } from "react";

import { Copy } from "components/icons/generated";
import Box from "ds/components/Box";
import ButtonIcon from "ds/components/ButtonIcon";
import useCopyToClipboard from "hooks/useCopyToClipboard";

import styles from "./styles.module.css";

type CodeSnippetBlockProps = {
  children: ReactNode;
  /** Raw text used for the copy action. Required when children isn't a plain string. */
  copyValue?: string;
  language?: string;
  allowCopy?: boolean;
  copyTitle?: string;
  onCopy?: () => void;
  className?: string;
  maxHeight?: string | number;
  ariaLabel?: string;
};

const CodeSnippetBlock = ({
  children,
  copyValue,
  language,
  allowCopy = false,
  copyTitle = "Copy",
  onCopy,
  className,
  maxHeight,
  ariaLabel,
}: CodeSnippetBlockProps) => {
  const textToCopy = copyValue ?? (typeof children === "string" ? children : "");
  const handleCopy = useCopyToClipboard(textToCopy, onCopy);
  const showHeader = !!language || allowCopy;
  const label = ariaLabel || (language ? `${language} code snippet` : "Code snippet");

  return (
    <div className={cx(styles.block, className)}>
      {showHeader && (
        <Box
          direction="row"
          align="center"
          gap="medium"
          padding="small small small x-large"
          className={styles.header}
        >
          {!!language && <span>{language}</span>}
          {allowCopy && (
            <ButtonIcon
              className={styles.copyButton}
              size="small"
              variant="ghost"
              icon={Copy}
              onPress={handleCopy}
            >
              {copyTitle}
            </ButtonIcon>
          )}
        </Box>
      )}
      <pre
        className={styles.pre}
        tabIndex={0}
        role="group"
        aria-label={label}
        style={maxHeight !== undefined ? { maxHeight } : undefined}
      >
        <code className={styles.code}>{children}</code>
      </pre>
    </div>
  );
};

CodeSnippetBlock.displayName = "DS.CodeSnippetBlock";

export default CodeSnippetBlock;
